import { CommentMapLink, CommentMapPosition, CommentNode } from "./types";

export interface CommentLayoutOptions {
  columnWidth?: number;
  rowHeight?: number;
  nodeWidth?: number;
  nodeHeight?: number;
  rootGap?: number;
}

export interface CommentLayoutResult {
  positions: CommentMapPosition[];
  links: CommentMapLink[];
  width: number;
  height: number;
}

const DEFAULT_OPTIONS: Required<CommentLayoutOptions> = {
  columnWidth: 260,
  rowHeight: 128,
  nodeWidth: 210,
  nodeHeight: 96,
  rootGap: 56,
};

/**
 * Simple deterministic V1 tree layout for CommentMapView.
 * This intentionally avoids a heavy graph dependency. It gives each depth
 * a column and each visible comment a row, which is good enough for the
 * first Comment Explorer canvas implementation.
 */
export function layoutCommentTree(
  roots: CommentNode[],
  options: CommentLayoutOptions = {}
): CommentLayoutResult {
  const cfg = { ...DEFAULT_OPTIONS, ...options };
  const positions: CommentMapPosition[] = [];
  const links: CommentMapLink[] = [];
  let row = 0;
  let maxDepth = 0;

  const place = (node: CommentNode, depth: number) => {
    maxDepth = Math.max(maxDepth, depth);
    positions.push({
      id: node.id,
      x: depth * cfg.columnWidth,
      y: row * cfg.rowHeight,
      width: cfg.nodeWidth,
      height: cfg.nodeHeight,
    });
    row += 1;

    node.replies.forEach((reply) => {
      links.push({ fromId: node.id, toId: reply.id });
      place(reply, depth + 1);
    });
  };

  roots.forEach((root, index) => {
    if (index > 0) row += Math.max(1, Math.round(cfg.rootGap / cfg.rowHeight));
    place(root, 0);
  });

  return {
    positions,
    links,
    width: maxDepth * cfg.columnWidth + cfg.nodeWidth,
    height: Math.max(0, row - 1) * cfg.rowHeight + cfg.nodeHeight,
  };
}
